/**
 * CoreHandoffPage.tsx — /auth-core, the GPS Core → Clinics sign-in handoff.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: Clinics-in-Core (Clinics ADR-1,
 * 2026-09-07).
 *
 * A patient signed in to GPS Core reaches the Clinics workspace by a
 * full-document link `<core-host>/clinics/auth-core#token=<Core sign-in token>`.
 * This page — Clinics' own code, on the Clinics-served document — reads that
 * token from the URL FRAGMENT once, scrubs it from the address bar, posts it to
 * identity's exchange lane and, on success, holds the ordinary Clinics session
 * identity mints and goes to the patient's own portal. Nothing GPS Core ships
 * ever writes to `gps.session`; this handoff is the only bridge, and it is
 * one-way.
 *
 * FRAGMENT, NEVER QUERY — the same discipline as /auth-pharmacy and /resubscribe
 * (lib/hashToken.ts): a query string is written to load-balancer, WAF and nginx
 * access logs on the very first load and cannot be retracted; a fragment never
 * leaves the browser. It is scrubbed the moment it is read so it does not
 * survive in history, a screen-share or a copied URL either.
 *
 * WHO CAN USE IT. A patient who ALREADY has a Clinics account: identity links
 * the verified email of the Core sign-in to the existing Clinics patient and pins
 * that link. Never staff — identity refuses, and clinicians and admins sign in
 * at /auth with their Clinics credentials. A patient with no Clinics account is
 * NOT an error state: they are offered the assessment on the standalone portal,
 * which is where every new patient starts.
 *
 * UNIFORM FAILURE for everything else — an expired or reused token, a locked
 * account, a lane that is switched off: one message, one action (go back to
 * Consultations and try again). Telling them apart would make this page an
 * oracle for whoever holds a leaked link. The 403 "no account yet" case is the
 * one distinction, made on STATUS alone; no response body is ever read.
 *
 * STANDALONE BUILDS: the route exists in every build (one route table,
 * routes.tsx) but has no purpose off Core, so it sends the visitor to /auth.
 * R28: category-level copy only — no medicine is named here.
 */
import { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { ApiError } from '../api/client';
import { exchangeCoreToken } from '../api/identity';
import { hostReturnPath, isCoreHosted, publicPortalOrigin } from '../config';
import { readHashToken, scrubHashFromAddressBar } from '../lib/hashToken';
import { Alert, ButtonArrow, Help, PageFrame, Spinner, Title, buttonClass, buttonStyle } from '../ui/kit';
import { useAuth } from './AuthContext';
import { homeForRole } from './roleHome';
import { setToken } from './session';

type Phase = 'exchanging' | 'no-token' | 'not-provisioned' | 'failed';

/** The one copy for every refusal that is not "no account yet" (see the module doc). */
const UNIFORM_FAILURE =
  'We could not open GPS Clinics from your account just now. Go back to Consultations and try again.';

export function CoreHandoffPage() {
  const navigate = useNavigate();
  const { me, status, refresh } = useAuth();
  // Read the fragment ONCE, during the first render, before anything can
  // navigate: the scrub below removes it from the URL, so a later read would
  // find nothing.
  const [token] = useState<string | null>(() =>
    typeof window === 'undefined' ? null : readHashToken(window.location.hash),
  );
  const [phase, setPhase] = useState<Phase>(token ? 'exchanging' : 'no-token');

  // Scrub the credential out of the address bar the moment we hold it.
  useEffect(() => {
    if (token) scrubHashFromAddressBar();
  }, [token]);

  // The token is SINGLE-USE at identity, so it must be posted exactly ONCE — and
  // React StrictMode (dev) runs every effect twice on mount. A started-flag in a
  // ref (never reset on cleanup) makes the second invocation a no-op; the first
  // run is left to finish and navigate rather than being cancelled, because a
  // cancelled first run would leave a minted session in the store and this page
  // reporting failure over it.
  const started = useRef(false);
  useEffect(() => {
    if (!isCoreHosted() || !token || started.current) return;
    started.current = true;
    void (async () => {
      try {
        const session = await exchangeCoreToken({ idToken: token });
        setToken(session.sessionToken);
        // Hydrate `me` from /auth/me so the portal opens with the full principal
        // (name, verification state) exactly as a password login would.
        await refresh();
        navigate(homeForRole(session.role), { replace: true });
      } catch (error) {
        setPhase(error instanceof ApiError && error.status === 403 ? 'not-provisioned' : 'failed');
      }
    })();
  }, [token, navigate, refresh]);

  // Off Core this page has no job — the standalone front door is /auth.
  if (!isCoreHosted()) return <Navigate to="/auth" replace />;

  // Arrived without a fresh token (a bookmark, a Back navigation): wait for the
  // session to hydrate rather than flashing the "how to get in" copy at someone
  // who is already signed in — then straight to their own portal.
  if (!token && status === 'loading') {
    return (
      <PageFrame narrow>
        <Spinner label="Checking your session…" />
      </PageFrame>
    );
  }
  if (!token && me) return <Navigate to={homeForRole(me.role)} replace />;

  const backHref = hostReturnPath();
  const assessmentHref = publicPortalOrigin() ? `${publicPortalOrigin()}/weightloss` : null;

  return (
    <PageFrame narrow>
      <Title>Opening GPS Clinics</Title>

      {phase === 'exchanging' ? <Spinner label="Signing you in to GPS Clinics…" /> : null}

      {phase === 'no-token' ? (
        // Nothing to exchange: the visitor did not arrive through the Core switch
        // (or the fragment was lost). Reveals nothing — the browser can see its
        // own URL either way.
        <>
          <Help>To open GPS Clinics, use the Clinics option from your Consultations dashboard.</Help>
          <a href={backHref} className={buttonClass('cta')} style={buttonStyle('cta')}>
            Back to Consultations
            <ButtonArrow />
          </a>
        </>
      ) : null}

      {phase === 'not-provisioned' ? (
        <>
          <Help>
            Your account is not set up with GPS Clinics yet. If you would like to start, you can check
            your eligibility on the clinic&rsquo;s own site.
          </Help>
          {assessmentHref ? (
            <a href={assessmentHref} className={buttonClass('cta')} style={buttonStyle('cta')}>
              Check your eligibility
              <ButtonArrow />
            </a>
          ) : null}
          <p style={{ marginTop: 'var(--gps-s-3)' }}>
            <a href={backHref} style={{ color: 'var(--gps-link)' }}>
              Back to Consultations
            </a>
          </p>
        </>
      ) : null}

      {phase === 'failed' ? (
        <>
          <Alert tone="danger" role="alert">
            {UNIFORM_FAILURE}
          </Alert>
          <a href={backHref} className={buttonClass('cta')} style={buttonStyle('cta')}>
            Back to Consultations
            <ButtonArrow />
          </a>
        </>
      ) : null}
    </PageFrame>
  );
}
